import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  Upload, Image as ImageIcon, X, CheckCircle2, AlertTriangle,
  Loader2, Crop, Trash2, FileImage, Shield, Info, ZoomIn,
} from 'lucide-react';

import { calcularHash } from '../../services/AssetPericialService';
import { LIMITES } from '../../constants/vistoria';

/**
 * ============================================================
 * COMPONENTE: UploadPlanta
 * ------------------------------------------------------------
 * Upload da planta/croqui do imóvel com:
 *   ✔ Drag & drop
 *   ✔ Colar da área de transferência (Ctrl+V)
 *   ✔ Preview com zoom
 *   ✔ Validação de tipo e tamanho
 *   ✔ Hash SHA-256 para integridade
 *   ✔ Persistência via callback (onChange)
 * VERSÃO: 1.0.0
 * ============================================================
 */

const TIPOS_ACEITOS = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
];

const EXTENSOES_ACEITAS = ['.jpg', '.jpeg', '.png', '.webp', '.heic'];

/* =========================
   HELPERS
========================= */

function validarArquivo(file) {
  if (!file) return 'Nenhum arquivo recebido.';

  const tipoOk =
    TIPOS_ACEITOS.includes(file.type) ||
    EXTENSOES_ACEITAS.some(ext =>
      file.name.toLowerCase().endsWith(ext)
    );

  if (!tipoOk) {
    return `Formato "${file.type || 'desconhecido'}" não aceito. Use JPG, PNG, WEBP ou HEIC.`;
  }

  const mb = file.size / 1024 / 1024;
  if (mb > LIMITES.ARQUIVO_MAX_MB) {
    return `Arquivo de ${mb.toFixed(2)}MB excede o limite de ${LIMITES.ARQUIVO_MAX_MB}MB.`;
  }

  return null;
}

/* =========================
   COMPONENTE
========================= */

export default function UploadPlanta({
  plantaImg = null,
  plantaMeta = null,
  onChange,
  onRemover,
  reportNumber = 'REPORT-2026-MH',
  compacto = false,
}) {
  const [dragover, setDragover] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [erro, setErro] = useState(null);
  const [progresso, setProgresso] = useState(null);
  const [zoomAberto, setZoomAberto] = useState(false);

  const fileInputRef = useRef(null);

  /* ---------- colar da área de transferência ---------- */
  useEffect(() => {
    const handlePaste = (e) => {
      // Só processa se não estiver digitando em input/textarea
      const alvo = e.target;
      if (alvo?.tagName === 'INPUT' || alvo?.tagName === 'TEXTAREA') return;

      const itens = Array.from(e.clipboardData?.items || []);
      const itemImagem = itens.find(i => i.type.startsWith('image/'));
      if (!itemImagem) return;

      const file = itemImagem.getAsFile();
      if (file) {
        e.preventDefault();
        processarArquivo(file);
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- processamento ---------- */
  const processarArquivo = useCallback(async (file) => {
    setErro(null);

    const problema = validarArquivo(file);
    if (problema) {
      setErro(problema);
      return;
    }

    setProcessando(true);
    setProgresso({ etapa: 'lendo', mensagem: 'Lendo arquivo...' });

    try {
      // 1. Lê bytes
      const buffer = await file.arrayBuffer();

      // 2. Hash SHA-256 (integridade)
      setProgresso({ etapa: 'hash', mensagem: 'Gerando hash SHA-256...' });
      const hash = await calcularHash(buffer);

      // 3. Converte para base64 (persistível)
      setProgresso({ etapa: 'base64', mensagem: 'Preparando preview...' });
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Falha ao converter imagem'));
        reader.readAsDataURL(file);
      });

      // 4. Obtém dimensões
      setProgresso({ etapa: 'dimensoes', mensagem: 'Medindo dimensões...' });
      const dimensoes = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ largura: img.width, altura: img.height });
        img.onerror = () => resolve({ largura: 0, altura: 0 });
        img.src = base64;
      });

      const meta = {
        nomeOriginal: file.name,
        tamanhoBytes: file.size,
        tipo: file.type || 'image/jpeg',
        hashIntegridade: hash,
        largura: dimensoes.largura,
        altura: dimensoes.altura,
        megapixels: +(
          (dimensoes.largura * dimensoes.altura) / 1_000_000
        ).toFixed(2),
        uploadedEm: new Date().toISOString(),
        reportNumber,
      };

      setProgresso({ etapa: 'ok', mensagem: 'Planta carregada' });

      if (typeof onChange === 'function') {
        onChange({ plantaImg: base64, plantaMeta: meta });
      }

      // Limpa progresso após 1s
      setTimeout(() => setProgresso(null), 1000);
    } catch (err) {
      setErro(err.message || 'Falha ao processar planta.');
      setProgresso(null);
    } finally {
      setProcessando(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  }, [onChange, reportNumber]);

  /* ---------- drag & drop ---------- */
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!processando) setDragover(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragover(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    if (processando) return;

    const file = e.dataTransfer?.files?.[0];
    if (file) processarArquivo(file);
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processarArquivo(file);
  };

  /* ---------- remoção ---------- */
  const removerPlanta = () => {
    if (!window.confirm('Remover a planta atual? Os pins já marcados serão mantidos, mas perderão a referência visual.')) {
      return;
    }
    if (typeof onRemover === 'function') {
      onRemover();
    } else if (typeof onChange === 'function') {
      onChange({ plantaImg: null, plantaMeta: null });
    }
  };

  /* =========================
     RENDER — COM PLANTA CARREGADA
  ========================= */

  if (plantaImg) {
    return (
      <>
        <div className="bg-slate-900 rounded-[2rem] border border-white/5 p-4 flex flex-wrap items-center gap-4">
          {/* THUMB */}
          <div
            className="w-16 h-16 rounded-xl overflow-hidden border border-white/10 bg-slate-950 shrink-0 cursor-pointer group"
            onClick={() => setZoomAberto(true)}
            title="Ampliar planta"
          >
            <img
              src={plantaImg}
              alt="Planta do imóvel"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          </div>

          {/* METADADOS */}
          <div className="flex-1 min-w-0 space-y-0.5">
            <span className="text-[9px] text-emerald-400 tracking-[0.3em] flex items-center gap-2">
              <CheckCircle2 size={10} />
              PLANTA CARREGADA
            </span>
            <p className="text-white text-[12px] tracking-tight truncate normal-case">
              {plantaMeta?.nomeOriginal || 'planta.jpg'}
            </p>
            <p className="text-slate-500 text-[9px] tracking-widest font-mono">
              {plantaMeta?.largura}×{plantaMeta?.altura}px
              {' · '}
              {plantaMeta?.megapixels}MP
              {' · '}
              {plantaMeta?.tamanhoBytes
                ? `${(plantaMeta.tamanhoBytes / 1024 / 1024).toFixed(2)}MB`
                : '—'}
            </p>
          </div>

          {/* HASH BADGE */}
          {plantaMeta?.hashIntegridade && (
            <div
              className="hidden lg:flex items-center gap-2 bg-slate-950 border border-white/5 rounded-xl px-3 py-2"
              title={`Hash SHA-256: ${plantaMeta.hashIntegridade}`}
            >
              <Shield size={12} className="text-amber-400" />
              <span className="text-[9px] text-slate-400 tracking-widest font-mono">
                {plantaMeta.hashIntegridade.substring(0, 12)}...
              </span>
            </div>
          )}

          {/* AÇÕES */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setZoomAberto(true)}
              className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-all"
              title="Ampliar"
            >
              <ZoomIn size={14} />
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={processando}
              className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-all disabled:opacity-50"
              title="Substituir planta"
            >
              <Crop size={14} />
            </button>
            <button
              type="button"
              onClick={removerPlanta}
              className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-500/30 flex items-center justify-center transition-all"
              title="Remover planta"
            >
              <Trash2 size={14} />
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            className="hidden"
          />
        </div>

        {/* ERRO */}
        {erro && (
          <div className="mt-3 bg-red-950/50 border border-red-500/30 rounded-2xl p-4 flex items-start gap-3">
            <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
            <span className="text-red-200 text-[10px] tracking-wide normal-case flex-1">
              {erro}
            </span>
          </div>
        )}

        {/* MODAL DE ZOOM */}
        {zoomAberto && (
          <div
            className="fixed inset-0 z-[500] bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-6"
            onClick={() => setZoomAberto(false)}
          >
            <button
              type="button"
              onClick={() => setZoomAberto(false)}
              className="absolute top-6 right-6 w-12 h-12 rounded-2xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
            >
              <X size={20} />
            </button>
            <img
              src={plantaImg}
              alt="Planta ampliada"
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </>
    );
  }

  /* =========================
     RENDER — SEM PLANTA (DROPZONE)
  ========================= */

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !processando && fileInputRef.current?.click()}
      className={`relative cursor-pointer bg-slate-900 border-2 border-dashed rounded-[2rem] transition-all ${
        dragover
          ? 'border-amber-400 bg-amber-500/5 scale-[1.01]'
          : 'border-white/10 hover:border-amber-400/50 hover:bg-slate-900/80'
      } ${compacto ? 'p-6' : 'p-10'}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />

      <div className={`flex ${compacto ? 'items-center gap-4' : 'flex-col items-center gap-4 text-center'}`}>

        {/* ÍCONE */}
        <div className={`bg-slate-950 border border-white/10 rounded-2xl flex items-center justify-center shrink-0 ${
          compacto ? 'w-14 h-14' : 'w-20 h-20'
        }`}>
          {processando ? (
            <Loader2 size={compacto ? 22 : 32} className="text-amber-400 animate-spin" />
          ) : dragover ? (
            <Upload size={compacto ? 22 : 32} className="text-amber-400" />
          ) : (
            <FileImage size={compacto ? 22 : 32} className="text-slate-500" />
          )}
        </div>

        {/* TEXTO */}
        <div className={`space-y-1 ${compacto ? 'flex-1 min-w-0' : ''}`}>
          <p className={`text-white tracking-tight leading-tight ${
            compacto ? 'text-[13px]' : 'text-[16px]'
          }`}>
            {processando
              ? progresso?.mensagem || 'Processando...'
              : dragover
                ? 'Solte a planta aqui'
                : 'Upload da Planta / Croqui'}
          </p>

          {!processando && !dragover && (
            <>
              <p className={`text-slate-500 tracking-widest ${
                compacto ? 'text-[9px]' : 'text-[10px]'
              }`}>
                ARRASTE · CLIQUE · OU CTRL+V PARA COLAR
              </p>
              <p className="text-slate-600 text-[9px] tracking-widest">
                JPG · PNG · WEBP · HEIC · MÁX {LIMITES.ARQUIVO_MAX_MB}MB
              </p>
            </>
          )}

          {processando && progresso && (
            <p className="text-slate-500 text-[9px] tracking-widest">
              {progresso.etapa?.toUpperCase()}
            </p>
          )}
        </div>

        {/* INFO SEGURANÇA (só no modo expandido) */}
        {!compacto && !processando && !dragover && (
          <div className="mt-2 pt-4 border-t border-white/5 w-full flex items-center justify-center gap-3">
            <Shield size={12} className="text-amber-400/60" />
            <span className="text-[9px] text-slate-500 tracking-widest">
              HASH SHA-256 · MARCA D'ÁGUA · INTEGRIDADE NBR 14653
            </span>
          </div>
        )}
      </div>

      {/* ERRO */}
      {erro && (
        <div className="mt-4 bg-red-950/50 border border-red-500/30 rounded-2xl p-4 flex items-start gap-3 text-left">
          <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
          <span className="text-red-200 text-[10px] tracking-wide normal-case flex-1">
            {erro}
          </span>
        </div>
      )}
    </div>
  );
}